import React from 'react';
import { Box, Typography, Chip, useTheme } from '@mui/material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { requestGetProjectSummary } from '../redux/sagas/requests/project';
import { statusColor } from '../utils/statusColors';

const StatusRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
        borderBottom: 'none',
    },
}));

const StatusLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontSize: theme.typography.size.md,
}));

const StatusChip = styled(Chip)(({ theme, statuscolor }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    backgroundColor: statuscolor,
    color: theme.palette.getContrastText(statuscolor),
    minWidth: 40,
}));

const TotalRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 0, 0),
    marginTop: theme.spacing(1),
    borderTop: `2px solid ${theme.palette.divider}`,
}));

const TotalLabel = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.size.base,
}));

const TotalValue = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.size.lg,
    color: theme.palette.primary.main,
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px',
    color: theme.palette.text.secondary,
}));

const ActiveProjectsWidget = ({ widgetId, config = {}, onRemove }) => {
    const theme = useTheme();

    const { data: projectStats, isLoading, error } = useFetchQueue(widgetId, {
        apiCall: () => requestGetProjectSummary(),
        cacheKey: 'project-summary',
        transform: (response) => {
            return response.reduce((acc, statusStat) => {
                if (statusStat.status !== 'Completed') {
                    acc[statusStat.status] = statusStat.count;
                }
                return acc;
            }, {});
        },
    });

    const totalProjects = Object.values(projectStats || {}).reduce((sum, count) => sum + count, 0);
    const hasData = totalProjects > 0;

    return (
        <BaseWidget
            title="Active Projects"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
        >
            {!hasData ? (
                <EmptyState>
                    <Typography variant="body2">
                        No projects found
                    </Typography>
                </EmptyState>
            ) : (
                <Box>
                    {Object.entries(projectStats || {}).map(([status, count]) => (
                        <StatusRow key={status}>
                            <StatusLabel>{status}</StatusLabel>
                            <StatusChip
                                label={count}
                                size="small"
                                statuscolor={statusColor(theme, 'project', status)}
                            />
                        </StatusRow>
                    ))}
                    <TotalRow>
                        <TotalLabel>Total Projects</TotalLabel>
                        <TotalValue>{totalProjects}</TotalValue>
                    </TotalRow>
                </Box>
            )}
        </BaseWidget>
    );
};

export default ActiveProjectsWidget;
